import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Loader2 } from "lucide-react";
import { api, resolveMedia } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { SectionLabel } from "../components/SectionLabel";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });
  useSEO("It's Yours", "Your Queen Denae Royal Vault download.");

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error" });
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", ...data });
          return;
        }
      } catch {}
      if (++tries < 30) {
        timer = setTimeout(poll, 2000);
      } else {
        setState({ phase: "timeout" });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="pt-40 pb-32 min-h-screen">
      <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
        <SectionLabel center>The Royal Vault</SectionLabel>
        {state.phase === "checking" && (
          <div>
            <Loader2 className="mx-auto animate-spin text-gold" size={32} />
            <p className="mt-6 font-serif text-3xl text-cream">Confirming your payment…</p>
            <p className="mt-3 text-sand text-sm">One moment while Stripe seals the envelope.</p>
          </div>
        )}
        {state.phase === "paid" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-cream">
              IT'S <span className="italic text-goldglow">YOURS.</span>
            </h1>
            <p className="mt-6 text-lg text-sand">
              {state.title ? <>You now own <span className="text-cream">{state.title}</span> — forever, no subscription.</> : "You own this piece — forever, no subscription."}
            </p>
            <a
              href={resolveMedia(state.download_url)}
              data-testid="download-purchased-track-btn"
              className="mt-10 inline-flex h-14 px-10 items-center gap-3 bg-gold text-obsidian font-mono text-[11px] tracking-[0.3em] uppercase hover:bg-goldglow transition-colors"
            >
              <Download size={15} /> Download Your Song
            </a>
            <p className="mt-5 text-xs text-sand/60 max-w-sm mx-auto leading-relaxed">
              Save this page or the file itself — your download link works up to 10 times. Thank you for supporting independent music, straight from the Queen.
            </p>
            <Link to="/vault" className="mt-8 inline-block font-mono text-[10px] tracking-[0.3em] uppercase text-gold hover:text-goldglow">
              ← Back to the Vault
            </Link>
          </motion.div>
        )}
        {(state.phase === "timeout" || state.phase === "error") && (
          <div>
            <p className="font-serif text-3xl text-cream">We're confirming your payment.</p>
            <p className="mt-4 text-sand text-sm leading-relaxed">
              Stripe can take a moment. If you completed checkout, your download will be ready shortly — refresh this page in a minute.
            </p>
            <button
              onClick={() => window.location.reload()}
              data-testid="payment-refresh-btn"
              className="mt-8 h-12 px-8 border border-gold/50 text-gold font-mono text-[10px] tracking-[0.3em] uppercase hover:bg-gold hover:text-obsidian transition-colors"
            >
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
